import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Download, History, ScrollText } from 'lucide-react';
import { formatCents, formatDate, type OwnedHistoryLease } from '@vuokramani/shared';
import { PageHeader, Section } from '../../components/page.tsx';
import { buttonClass, EmptyState, Panel, Splash } from '../../components/ui.tsx';
import {
  accountExportUrl,
  leaseExportUrl,
  propertyExportUrl,
  useOwnedHistory,
} from '../../lib/history.ts';
import { depositPdfUrl } from '../../lib/tenancy.ts';
import l from '../shared/logs.module.css';
import h from './history.module.css';

/**
 * Vuokranantajan historia: asunnoittain päättyneet vuokrasuhteet (vuokralaiset, aika,
 * irtisanoja, vuokra, vakuus, maksamatta jääneet) sekä aineisto zipinä vuokrasuhde,
 * asunto tai kaikki asunnot kerrallaan.
 */
export function LandlordHistoryPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useOwnedHistory();
  if (isLoading) return <Splash />;
  const properties = data?.properties ?? [];

  return (
    <>
      <PageHeader
        title={t('nav.history')}
        actions={
          <a className={buttonClass('primary')} href={accountExportUrl()} download>
            <Download size={16} aria-hidden="true" />
            {t('history.downloadAll')}
          </a>
        }
      />
      <p className={l.note}>{t('history.note')}</p>
      {properties.length === 0 ? (
        <EmptyState
          icon={<History size={22} />}
          title={t('history.emptyTitle')}
          text={t('history.emptyText')}
        />
      ) : (
        <div className={h.list}>
          {properties.map((p) => (
            <Section
              key={p.propertyId}
              title={p.archived ? `${p.title} (${t('financePage.archived')})` : p.title}
              note={p.address}
              action={
                <a className={h.exportLink} href={propertyExportUrl(p.propertyId)} download>
                  <Download size={14} aria-hidden="true" />
                  {t('history.propertyZip')}
                </a>
              }
            >
              <Panel>
                {p.leases.length === 0 ? (
                  <p className={h.empty}>{t('history.noEndedLeases')}</p>
                ) : (
                  p.leases.map((lease) => <LeaseRow key={lease.leaseId} lease={lease} />)
                )}
              </Panel>
            </Section>
          ))}
        </div>
      )}
    </>
  );
}

function LeaseRow({ lease }: { lease: OwnedHistoryLease }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const money = (cents: number) => formatCents(cents, lang);
  const period = `${formatDate(lease.startDate, lang)} – ${
    lease.endDate ? formatDate(lease.endDate, lang) : t('invoices.untilFurther')
  }`;
  const first = [
    lease.tenants.join(', ') || '–',
    period,
    lease.terminatedBy ? t(`ending.by.${lease.terminatedBy}`) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const deposit =
    lease.depositReturnedOn !== null
      ? t('history.depositReturned', {
          returned: money(lease.depositReturnedCents ?? 0),
          deposit: money(lease.depositCents),
          date: formatDate(lease.depositReturnedOn, lang),
        }) + (lease.depositNote ? ` (${lease.depositNote})` : '')
      : lease.depositCents > 0
        ? t('history.depositNotReturned', { deposit: money(lease.depositCents) })
        : null;
  const second = [t('history.rent', { rent: money(lease.rentCents) }), deposit]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={l.row}>
      <ScrollText size={18} aria-hidden="true" className={l.icon} />
      <div className={l.text}>
        <span className={l.titleStrong}>{first}</span>
        <span className={l.meta}>
          {second} ·{' '}
          {lease.unpaidCents > 0 ? (
            <span className={h.unpaid}>
              {t('history.unpaid', { amount: money(lease.unpaidCents) })}
            </span>
          ) : (
            t('history.allPaid')
          )}
        </span>
      </div>
      <div className={l.actions}>
        <Link to={`/vuokranantaja/loki/vuokrasuhde/${lease.leaseId}`}>{t('log.title')}</Link>
        {lease.depositReturnedOn && (
          <a href={depositPdfUrl(lease.leaseId)} download>
            {t('deposit.pdfShort')}
          </a>
        )}
        <a href={leaseExportUrl(lease.leaseId)} download>
          <Download size={14} aria-hidden="true" />
          zip
        </a>
      </div>
    </div>
  );
}
